import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, useAuth, setActiveCurrency } from '@halo/web-lib';

/**
 * Keeps the web-lib ambient display currency (used by formatCurrency) in sync
 * with the active store's currency, so every money value across the dashboard
 * renders in the connected shop's currency (e.g. AUD) instead of a hardcoded
 * USD symbol.
 *
 * Single active store at a time → a module-level ambient currency is sufficient
 * and avoids threading a currency arg through ~150 call sites. Multi-store views
 * (agency tables) pass an explicit currency to formatCurrency to override it.
 *
 * The value is also persisted to localStorage by setActiveCurrency, so a hard
 * refresh paints the correct currency before this effect runs.
 */
export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { storeId, isAuthenticated } = useAuth();

  const { data: stores } = useQuery({
    queryKey: ['stores'],
    queryFn: async ({ signal }) => {
      const res = await api.get<Array<{ id: number; currency?: string }>>('/api/v1/auth/stores', {
        signal,
      });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load stores');
      return res.data;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!storeId || !stores) return;
    const active = stores.find((s) => String(s.id) === String(storeId));
    if (active?.currency) setActiveCurrency(active.currency);
  }, [storeId, stores]);

  return <>{children}</>;
}
